import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useHistory } from 'react-router-dom';
import io from 'socket.io-client';
import { AUTH_TOKEN } from 'constant';
import LocalStore from 'helpers/localStore';
import {
  useCreateConversationMutation,
  useSearchUsersQuery,
  useGetMyAccountQuery,
  useGetMyConversationsQuery,
  useUpdateConversationMutation,
  useGetAllMessagesQuery,
  useRemoveMessagesMutation,
  useLeaveConversationMutation,
} from 'graphql/types';

import type { Conversation, Message } from '../types';
import { getMessageThreadPath } from '../messageUrl';
import { mapLastMessageFromApi, sortConversations } from '../conversationListUtils';

type AvailableUser = {
  id: string;
  username?: string;
  firstname?: string;
  lastname?: string;
  avatarUrl?: string;
};

type ChatContextValue = {
  user: { id: number; username: string } | null;
  availableUsers: AvailableUser[];
  conversations: Conversation[];
  conversationsLoading: boolean;
  setConversations: React.Dispatch<React.SetStateAction<Conversation[]>>;
  activeConversation: Conversation | null;
  setActiveConversation: React.Dispatch<
    React.SetStateAction<Conversation | null>
  >;
  messages: Message[];
  messagesLoading: boolean;
  messageInput: string;
  setMessageInput: React.Dispatch<React.SetStateAction<string>>;
  onlineUserIds: number[];
  creatingConversation: boolean;
  updatingConversation: boolean;
  clearingHistory: boolean;
  leavingGroup: boolean;
  updatingGroup: boolean;
  handleSendMessage: () => void;
  handleStartPrivateChat: (params: { userId?: string; username?: string }) => Promise<void>;
  handleCreateGroup: (payload: { name: string; members: string[] }) => Promise<void>;
  handleJoinChannel: (payload: { groupId: string }) => Promise<void>;
  handleClearHistory: () => Promise<void>;
  handleLeaveGroup: () => Promise<void>;
  handleEditGroup: (payload: { id: string; name: string; members: string[] }) => Promise<void>;
  handleConversationSelect: (c: Conversation) => void;
  messagesContainerRef: React.RefObject<HTMLDivElement | null>;
  sortedMessages: Message[];
};

const ChatContext = createContext<ChatContextValue | null>(null);

export function ChatProvider({ children }: { children: React.ReactNode }) {
  const history = useHistory();
  const token = LocalStore.get(AUTH_TOKEN);
  const socketRef = useRef<any>(null);

  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [activeConversation, setActiveConversation] =
    useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [messageInput, setMessageInput] = useState('');
  const [onlineUserIds, setOnlineUserIds] = useState<number[]>([]);
  const [clearingHistory, setClearingHistory] = useState(false);
  const [leavingGroup, setLeavingGroup] = useState(false);
  const [updatingGroup, setUpdatingGroup] = useState(false);
  const activeConversationRef = useRef<Conversation | null>(null);
  const messagesContainerRef = useRef<HTMLDivElement | null>(null);
  const [removeMessages] = useRemoveMessagesMutation();
  const [leaveConversation] = useLeaveConversationMutation();

  const { data: accountData } = useGetMyAccountQuery();
  const user = useMemo(() => {
    const acc = accountData?.myAccount?.data;
    if (!acc) return null;
    return { id: Number(acc.id), username: acc.attributes?.username ?? '' };
  }, [accountData]);

  const {
    data: convData,
    loading: conversationsLoading,
    refetch: refetchConversations,
  } = useGetMyConversationsQuery();

  const { data: allUsersData } = useSearchUsersQuery({ variables: { search: '' } });

  const availableUsers = useMemo<AvailableUser[]>(
    () =>
      (allUsersData?.usersForAutocomplete ?? []).map((entry: any) => ({
        id: String(entry.id),
        username: entry.username ?? undefined,
        firstname: entry.firstname ?? undefined,
        lastname: entry.lastname ?? undefined,
        avatarUrl: entry.avatarUrl ?? undefined,
      })),
    [allUsersData],
  );

  useEffect(() => {
    if (!user) return;

    let active = true;

    const fetchOnline = async () => {
      try {
        const baseURL = process.env.REACT_APP_API_BASE_URL;
        if (!baseURL) return;
        const res = await fetch(`${baseURL}/api/online-users`);
        if (!res.ok) return;
        const data = await res.json();
        if (!active) return;
        setOnlineUserIds(data.userIds ?? []);
      } catch {
        if (!active) return;
        setOnlineUserIds([]);
      }
    };

    fetchOnline();
    const t = setInterval(fetchOnline, 20000);
    return () => {
      active = false;
      clearInterval(t);
    };
  }, [user]);

  useEffect(() => {
    if (!convData?.myConversations || !user) return;

    const list: Conversation[] = convData.myConversations.map((c: any) => {
      const entity = c.data ? c.data : c;
      const attrs = entity.attributes ?? entity;
      const isGroup = Boolean(attrs.is_group);
      const members = attrs.members?.data ?? [];
      const memberIds = members
        .map((m: any) => Number(m.id))
        .filter((id: number) => Number.isFinite(id));
      const otherMember = !isGroup
        ? members.find((m: any) => Number(m.id) !== user.id)
        : null;

      return {
        id: Number(entity.id),
        name: attrs.name ?? null,
        is_group: isGroup,
        other_username:
          otherMember?.attributes?.username ?? otherMember?.username ?? null,
        other_avatar_url:
          otherMember?.attributes?.avatarUrl ?? otherMember?.avatarUrl ?? null,
        other_user_id: otherMember ? Number(otherMember.id) : null,
        member_ids: memberIds,
        unread_count: 0,
        updated_at: attrs.updatedAt ?? null,
        ...(mapLastMessageFromApi(attrs.messages?.data) ?? {}),
      };
    });

    setConversations(sortConversations(list));
  }, [convData, user]);

  const activeConversationId = activeConversation?.id;
  const {
    data: messageData,
    loading: messagesLoading,
    refetch: refetchMessages,
  } = useGetAllMessagesQuery({
    fetchPolicy: 'cache-and-network',
    skip: !activeConversationId,
    variables: {
      filters: {
        conversation: { id: { eq: activeConversationId ? String(activeConversationId) : undefined } },
      },
      sort: ['createdAt:asc'],
    },
  });

  useEffect(() => {
    activeConversationRef.current = activeConversation;
  }, [activeConversation]);

  const sortedMessages = useMemo(
    () =>
      [...messages].sort((a, b) => a.created_at.localeCompare(b.created_at)),
    [messages],
  );

  useEffect(() => {
    const el = messagesContainerRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [sortedMessages]);

  useEffect(() => {
    if (!activeConversation) {
      setMessages([]);
      return;
    }

    const list: Message[] = (messageData?.messages?.data ?? []).map((m: any) => ({
      id: Number(m.id),
      sender_id: Number(m.attributes?.sender?.data?.id ?? 0),
      senderName: m.attributes?.sender?.data?.attributes?.username ?? '',
      senderAvatar: m.attributes?.sender?.data?.attributes?.avatarUrl ?? null,
      content: m.attributes?.content ?? '',
      created_at: m.attributes?.createdAt ?? '',
      status: m.attributes?.status ?? 'sent',
    }));

    setMessages(list);
  }, [activeConversation, messageData]);

  useEffect(() => {
    if (!token || !user || socketRef.current) return;

    const s = io(process.env.REACT_APP_API_BASE_URL as string, {
      transports: ['websocket', 'polling'],
    });

    s.on('connect', () => {
      s.emit('register', { userId: user.id });
      socketRef.current = s;
    });

    s.on('message', (raw: any) => {
      const msg = typeof raw === 'string' ? JSON.parse(raw) : raw;
      const active = activeConversationRef.current;

      if (msg.type === 'message') {
        const m = msg.message as {
          id: number;
          content: string;
          created_at: string;
          status: string;
          senderName: string;
          senderAvatar?: string | null;
          sender_id: number;
        };

        const lastMessagePatch = {
          last_message_content: m.content,
          last_message_at: m.created_at,
          last_message_sender_id: m.sender_id,
          last_message_sender_name: m.senderName,
        };

        if (active && msg.conversationId === active.id) {
          setMessages((prev) => {
            if (prev.some((p) => p.id === m.id)) return prev;
            return [...prev, { ...m, status: m.status ?? 'sent' } as Message];
          });
          setConversations((prev) =>
            sortConversations(
              prev.map((c) =>
                c.id === msg.conversationId ? { ...c, ...lastMessagePatch } : c,
              ),
            ),
          );
        } else {
          setConversations((prev) =>
            sortConversations(
              prev.map((c) =>
                c.id === msg.conversationId
                  ? {
                    ...c,
                    ...lastMessagePatch,
                    unread_count: (c.unread_count ?? 0) + 1,
                  }
                  : c,
              ),
            ),
          );
        }
      }

      if (
        msg.type === 'unread_in_chat' &&
        (!active || msg.conversationId !== active.id)
      ) {
        setConversations((prev) =>
          prev.map((c) =>
            c.id === msg.conversationId
              ? { ...c, unread_count: (c.unread_count ?? 0) + 1 }
              : c,
          ),
        );
      }

      if (msg.type === 'read_receipt' && active && msg.conversationId === active.id) {
        setMessages((prev) =>
          prev.map((m) =>
            msg.messageIds.includes(m.id)
              ? { ...m, status: 'read' as const }
              : m,
          ),
        );
      }

      if (msg.type === 'new_conversation') {
        refetchConversations();
      }
    });

    s.on('disconnect', () => {
      socketRef.current = null;
    });

    return () => {
      s.disconnect();
      socketRef.current = null;
    };
  }, [token, user, refetchConversations]);

  useEffect(() => {
    if (!socketRef.current || !user || !activeConversation) return;
    socketRef.current.emit('join', {
      userId: user.id,
      conversationId: activeConversation.id,
    });
  }, [user, activeConversation]);

  useEffect(() => {
    if (!socketRef.current || !user || !activeConversation) return;
    const unreadIds = messages
      .filter((m) => m.sender_id !== user.id && m.status !== 'read')
      .map((m) => m.id);
    if (unreadIds.length === 0) return;

    const t = setTimeout(() => {
      socketRef.current?.emit('mark_read', {
        conversationId: activeConversation.id,
        userId: user.id,
        messageIds: unreadIds,
      });
    }, 300);
    return () => clearTimeout(t);
  }, [user, activeConversation, messages]);

  const handleSendMessage = useCallback(() => {
    if (!socketRef.current || !activeConversation || !messageInput.trim() || !user)
      return;

    const content = messageInput.trim();
    const sentAt = new Date().toISOString();

    socketRef.current.emit('message', {
      conversationId: activeConversation.id,
      senderId: user.id,
      content,
    });

    setConversations((prev) =>
      sortConversations(
        prev.map((c) =>
          c.id === activeConversation.id
            ? {
              ...c,
              last_message_content: content,
              last_message_at: sentAt,
              last_message_sender_id: user.id,
              last_message_sender_name: user.username,
            }
            : c,
        ),
      ),
    );

    setMessageInput('');
  }, [activeConversation, messageInput, user]);

  const [createConv, { loading: creatingConversation }] = useCreateConversationMutation();
  const [updateConv, { loading: updatingConversation }] = useUpdateConversationMutation();

  const handleStartPrivateChat = useCallback(
    async (params: { userId?: string; username?: string }) => {
      if (!user) return;

      try {
        const userId = params.userId?.trim();
        const username = params.username?.trim();

        const { data } = await createConv({
          variables: {
            input: {
              name: username,
              isGroup: false,
              memberIds: [String(userId)],
            },
          },
        });

        const created = data?.createConversation?.data;
        const createdAttrs = created?.attributes;
        const createdMembers = createdAttrs?.members?.data ?? [];
        const otherMember = createdMembers.find(
          (member: any) => Number(member?.id) !== user.id,
        );

        const newConversation: Conversation = {
          id: Number(created?.id ?? 0),
          name: createdAttrs?.name ?? null,
          is_group: Boolean(createdAttrs?.is_group),
          other_username: otherMember?.attributes?.username ?? null,
          other_avatar_url: otherMember?.attributes?.avatarUrl ?? null,
          other_user_id: otherMember ? Number(otherMember.id) : null,
          member_ids: createdMembers
            .map((member: any) => Number(member?.id))
            .filter((id: number) => Number.isFinite(id)),
          unread_count: 0,
        };

        setActiveConversation(newConversation);
        await refetchConversations();
        history.push(getMessageThreadPath(newConversation));
      } catch (err) {
        console.error('Failed to create conversation:', err);
      }
    },
    [user, createConv, refetchConversations, history],
  );

  const handleCreateGroup = useCallback(
    async (payload: { name: string; members: string[] }) => {
      const { data } = await createConv({
        variables: {
          input: {
            name: payload.name,
            isGroup: true,
            memberIds: payload.members,
          },
        },
      });

      const created = data?.createConversation?.data;
      const createdAttrs = created?.attributes;

      const newGroup: Conversation = {
        id: Number(created?.id ?? 0),
        name: createdAttrs?.name ?? payload.name,
        is_group: Boolean(createdAttrs?.is_group ?? true),
        member_ids: (createdAttrs?.members?.data ?? [])
          .map((member: any) => Number(member?.id))
          .filter((id: number) => Number.isFinite(id)),
        unread_count: 0,
      };
      setActiveConversation(newGroup);

      await refetchConversations();
      history.push(getMessageThreadPath(newGroup));
    },
    [createConv, refetchConversations, history],
  );

  const handleJoinChannel = useCallback(
    async (payload: { groupId: string }) => {
      await updateConv({
        variables: {
          input: {
            conversationId: payload.groupId, name: ''
          }
        }
      });
      await refetchConversations();
    },
    [updateConv, refetchConversations],
  );

  const handleClearHistory = useCallback(async () => {
    if (!activeConversation) return;

    setClearingHistory(true);
    try {
      await removeMessages({
        variables: {
          conversationId: String(activeConversation.id)
        }
      });
      setMessages([]);
      await refetchMessages();
      setConversations((prev) =>
        sortConversations(
          prev.map((c) =>
            c.id === activeConversation.id
              ? {
                ...c,
                last_message_content: null,
                last_message_at: null,
                last_message_sender_id: null,
                last_message_sender_name: null,
              }
              : c,
          ),
        ),
      );
    } catch (err) {
      console.error('Failed to clear chat history:', err);
      throw err;
    } finally {
      setClearingHistory(false);
    }
  }, [activeConversation, refetchMessages, removeMessages]);

  const handleLeaveGroup = useCallback(async () => {
    if (!activeConversation?.is_group || !user) return;

    const conversationId = activeConversation.id;

    setLeavingGroup(true);
    try {
      await leaveConversation({
        variables: {
          conversationId: String(conversationId)
        },
      });

      setMessages([]);
      setActiveConversation(null);
      setConversations((prev) => prev.filter((c) => c.id !== conversationId));
      await refetchConversations();
      history.push('/messages');
    } catch (err) {
      console.error('Failed to leave group:', err);
      throw err;
    } finally {
      setLeavingGroup(false);
    }
  }, [
    activeConversation,
    user,
    leaveConversation,
    refetchConversations,
    history,
  ]);

  const handleEditGroup = useCallback(
    async (payload: { id: string; name: string; members: string[] }) => {
      setUpdatingGroup(true);
      try {
        const { data } = await updateConv({
          variables: {
            input: {
              conversationId: payload.id,
              name: payload.name,
              memberIds: payload.members,
            }
          },
        });
        const updatedAttrs = data?.updateConversation?.data?.attributes;
        const updatedMembers = updatedAttrs?.members?.data ?? [];
        setActiveConversation((prev) =>
          prev && String(prev.id) === payload.id
            ? {
              ...prev,
              name: updatedAttrs?.name ?? prev.name,
              member_ids: updatedMembers
                .map((m: any) => Number(m.id))
                .filter((id: number) => Number.isFinite(id)),
            }
            : prev,
        );
        setConversations((prev) =>
          prev.map((c) =>
            String(c.id) === payload.id
              ? {
                ...c,
                name: updatedAttrs?.name ?? c.name,
                member_ids: updatedMembers
                  .map((m: any) => Number(m.id))
                  .filter((id: number) => Number.isFinite(id)),
              }
              : c,
          ),
        );
        await refetchConversations();
      } catch (err) {
        console.error('Failed to update group:', err);
        throw err;
      } finally {
        setUpdatingGroup(false);
      }
    },
    [updateConv, refetchConversations],
  );

  const handleConversationSelect = useCallback((c: Conversation) => {
    setActiveConversation(c);
    setConversations((prev) =>
      prev.map((conv) =>
        conv.id === c.id ? { ...conv, unread_count: 0 } : conv,
      ),
    );
  }, []);

  const value: ChatContextValue = useMemo(
    () => ({
      user,
      availableUsers,
      conversations,
      conversationsLoading,
      setConversations,
      activeConversation,
      setActiveConversation,
      messages,
      messagesLoading,
      messageInput,
      setMessageInput,
      onlineUserIds,
      creatingConversation,
      updatingConversation,
      clearingHistory,
      leavingGroup,
      updatingGroup,
      handleSendMessage,
      handleStartPrivateChat,
      handleCreateGroup,
      handleJoinChannel,
      handleClearHistory,
      handleLeaveGroup,
      handleEditGroup,
      handleConversationSelect,
      messagesContainerRef,
      sortedMessages,
    }),
    [
      user,
      availableUsers,
      conversations,
      conversationsLoading,
      activeConversation,
      messages,
      messagesLoading,
      messageInput,
      onlineUserIds,
      creatingConversation,
      updatingConversation,
      clearingHistory,
      leavingGroup,
      updatingGroup,
      handleSendMessage,
      handleStartPrivateChat,
      handleCreateGroup,
      handleJoinChannel,
      handleClearHistory,
      handleLeaveGroup,
      handleEditGroup,
      handleConversationSelect,
      sortedMessages,
    ],
  );

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
}

export function useChat(): ChatContextValue {
  const ctx = useContext(ChatContext);
  if (!ctx) throw new Error('useChat must be used within ChatProvider');
  return ctx;
}
